<template>
  <div class="edit-site-container">
    <b-loading
      :is-full-page="false"
      :active="isInitializingSite"
      :can-cancel="false"
    ></b-loading>
    <b-notification
      auto-close
      type="is-success"
      :active.sync="showSuccess"
      :duration="3000"
      :aria-close-label="$t('close_notification')"
    >
      {{ successMessage }}
    </b-notification>
    <b-notification
      auto-close
      type="is-danger"
      :active.sync="showFail"
      :duration="5000"
      :aria-close-label="$t('close_notification')"
    >
      {{ failedMessage }}
    </b-notification>
    <div class="close-form" @click="handleClose">
      <img src="/img/closebtn.png" />
    </div>

    <div v-if="editableSite" class="edit-site-form-container">
      <!-- <div class="column"> -->
      <!-- </div> -->
      <div class="column">
        <h4>{{ $t(`${TRANSLATION_ROOT}.edit_site`) }}</h4>

        <b-field :label="this.$t(`${TRANSLATION_ROOT}.siteName`)">
          <b-input
            v-model="editableSite.siteName"
            :disabled="!canEditSite"
          ></b-input>
        </b-field>
        <b-field :label="this.$t(`${TRANSLATION_ROOT}.city`)">
          <b-input
            v-model="editableSite.city"
            :disabled="!canEditSite"
          ></b-input>
        </b-field>
        <b-field :label="this.$t(`${TRANSLATION_ROOT}.state`)">
          <b-input
            v-model="editableSite.state"
            :disabled="!canEditSite"
          ></b-input>
        </b-field>
        <b-field :label="this.$t(`${TRANSLATION_ROOT}.country`)">
          <b-input
            v-model="editableSite.country"
            :disabled="!canEditSite"
          ></b-input>
        </b-field>
        <b-field :label="$t('admin_page.features')">
          <div class="dij-site-feature-list">
            <b-checkbox
              v-model="editableSite.repairAssessmentEnabled"
              :disabled="!canEditSite"
            >
              {{ this.$t(`${TRANSLATION_ROOT}.rat`) }}
            </b-checkbox>
            <b-checkbox v-model="site.unitConversionEnabled">
              {{ 'Unit Conversion' }}
            </b-checkbox>
          </div>
        </b-field>

        <b-field :label="$t('notifications')">
          <div class="dij-site-notification-list">
            <b-checkbox
              v-model="editableSite.allRoleNotificationsEnabled"
              @input="syncRoleNotificationsFromAllRoles"
            >
              {{ this.$t(`${TRANSLATION_ROOT}.enable_all_role_notifications`) }}
            </b-checkbox>
            <div class="role-notification-sub-list">
              <b-checkbox
                v-model="editableSite.csmNotificationsEnabled"
                @input="syncAllRolesFromRoleNotifications"
              >
                {{ this.$t(`${TRANSLATION_ROOT}.enable_csm_notifications`) }}
              </b-checkbox>
              <b-checkbox
                v-model="editableSite.repairEngineerNotificationsEnabled"
                @input="syncAllRolesFromRoleNotifications"
              >
                {{
                  this.$t(
                    `${TRANSLATION_ROOT}.enable_repair_engineer_notifications`
                  )
                }}
              </b-checkbox>
              <b-checkbox
                v-model="editableSite.supervisorNotificationsEnabled"
                @input="syncAllRolesFromRoleNotifications"
              >
                {{
                  this.$t(`${TRANSLATION_ROOT}.enable_supervisor_notifications`)
                }}
              </b-checkbox>
              <b-checkbox
                v-model="editableSite.projectOwnerNotificationsEnabled"
                @input="syncAllRolesFromRoleNotifications"
              >
                {{
                  this.$t(
                    `${TRANSLATION_ROOT}.enable_project_owner_notifications`
                  )
                }}
              </b-checkbox>
              <b-checkbox
                v-model="editableSite.siteAdminNotificationsEnabled"
                @input="syncAllRolesFromRoleNotifications"
              >
                {{
                  this.$t(`${TRANSLATION_ROOT}.enable_site_admin_notifications`)
                }}
              </b-checkbox>
            </div>
          </div>
        </b-field>
      </div>
    </div>
    <div v-else class="edit-site-form-container">
      <div class="column">
        <h4>{{ $t(`${TRANSLATION_ROOT}.edit_site`) }}</h4>
        <p>{{ $t('na') }}</p>
      </div>
    </div>

    <div class="job-phases-section">
      <div class="section-header">
        <h4>Job Phases</h4>
        <b-button
          v-if="canManagePhases"
          type="is-info"
          size="is-small"
          icon-left="plus"
          @click="openAddPhaseModal"
        >
          Add Phase
        </b-button>
      </div>

      <b-table
        :data="jobPhases"
        :narrowed="true"
        :loading="isPhasesLoading"
        :paginated="jobPhases.length > 20"
        :per-page="20"
      >
        <template slot-scope="props">
          <b-table-column field="name" label="Phase Name" sortable>
            {{ props.row.name }}
          </b-table-column>

          <b-table-column label="Default" width="90" centered>
            <b-checkbox
              :value="props.row.isDefault"
              :disabled="!canManagePhases"
              @input="toggleDefaultPhase(props.row._id, $event)"
            />
          </b-table-column>

          <b-table-column label="" width="110" centered>
            <b-button
              v-if="canManagePhases"
              type="is-info"
              size="is-small"
              icon-left="pencil"
              style="margin-right: 4px"
              @click.stop="openEditPhaseModal(props.row)"
            />
            <b-button
              v-if="canManagePhases"
              type="is-danger"
              size="is-small"
              icon-left="delete"
              @click.stop="confirmDeletePhase(props.row)"
            />
          </b-table-column>
        </template>
        <template #empty>
          <div class="has-text-centered">No phases</div>
        </template>
      </b-table>
    </div>

    <AddJobPhaseModal
      v-if="isPhaseModalOpen"
      @close="isPhaseModalOpen = false"
      @onSave="onAddPhase"
    />

    <EditJobPhaseModal
      v-if="editingPhase"
      :phase="editingPhase"
      @close="editingPhase = null"
      @onSave="onRenamePhase"
    />

    <div class="delete-update-buttons">
      <b-button
        @click="this.deleteSite"
        type="is-danger"
        outlined
        :disabled="!canEditSite"
      >
        {{ this.$t(`${TRANSLATION_ROOT}.delete`) }}
      </b-button>
      <b-button
        @click="this.updateSite"
        type="is-info"
        style="margin-left: 8px"
        :disabled="!canEditSite"
      >
        {{ this.$t(`${TRANSLATION_ROOT}.update`) }}
      </b-button>
    </div>
  </div>
</template>

<script>
import AddJobPhaseModal from './JobPhases/AddJobPhaseModal';
import EditJobPhaseModal from './JobPhases/EditJobPhaseModal';
import {
  FETCH_JOB_PHASES,
  ADD_JOB_PHASE,
  DELETE_JOB_PHASE,
  UPDATE_JOB_PHASE,
} from '../../store/jobPhases/actions/actionTypes';
import { GET_JOB_PHASES } from '../../store/jobPhases/getters/getterTypes';
import adminPageConstants from '../../constants/adminPageConstants';

const TRANSLATION_ROOT = 'edit_site_page';
const ROLE_NOTIFICATION_FIELDS = [
  'csmNotificationsEnabled',
  'repairEngineerNotificationsEnabled',
  'supervisorNotificationsEnabled',
  'projectOwnerNotificationsEnabled',
  'siteAdminNotificationsEnabled',
];

export default {
  name: 'edit-site',

  components: { AddJobPhaseModal, EditJobPhaseModal },

  props: {
    site: Object,
  },

  data() {
    return {
      TRANSLATION_ROOT,
      showFail: false,
      showSuccess: false,
      isInitializingSite: true,
      successMessage: this.$t(`${TRANSLATION_ROOT}.successMessage`),
      failedMessage: this.$t(`${TRANSLATION_ROOT}.failedMessage`),
      editableSite: this.site ? { ...this.site } : null,
      isPhaseModalOpen: false,
      isPhasesLoading: false,
      editingPhase: null,
    };
  },

  computed: {
    jobPhases() {
      return this.$store.getters[GET_JOB_PHASES];
    },

    user() {
      return this.$store.state.currentUser || {};
    },

    canEditSiteAll() {
      return this.$store.getters.hasPermission('admin.site.edit_all');
    },

    canEditSitePrimary() {
      return this.$store.getters.hasPermission('admin.site.edit_primary');
    },

    canEditSite() {
      if (!this.editableSite || !this.editableSite._id) return false;
      if (this.canEditSiteAll) return true;
      if (!this.canEditSitePrimary) return false;

      const primaryLocation =
        typeof this.user.location === 'object'
          ? this.user.location?._id
          : this.user.location;

      return (
        String(primaryLocation || '') === String(this.editableSite._id || '')
      );
    },

    canManagePhases() {
      return this.canEditSite;
    },
  },

  async created() {
    try {
      await this.initializeEditableSite();
    } finally {
      this.isInitializingSite = false;
    }
  },

  watch: {
    site: {
      handler(nextSite) {
        if (!nextSite) return;
        this.editableSite = this.normalizeNotificationSettings({ ...nextSite });
        this.isInitializingSite = false;
      },
      deep: true,
    },
  },

  async mounted() {
    this.isPhasesLoading = true;
    await this.$store.dispatch(FETCH_JOB_PHASES, { siteId: this.site._id });
    this.isPhasesLoading = false;
  },

  methods: {
    normalizeNotificationSettings(nextSite) {
      const normalizedSite = { ...nextSite };

      ROLE_NOTIFICATION_FIELDS.forEach((field) => {
        normalizedSite[field] = !!normalizedSite[field];
      });

      normalizedSite.allRoleNotificationsEnabled =
        ROLE_NOTIFICATION_FIELDS.every((field) => !!normalizedSite[field]);

      return normalizedSite;
    },

    syncRoleNotificationsFromAllRoles(enabled) {
      if (!this.editableSite) return;

      ROLE_NOTIFICATION_FIELDS.forEach((field) => {
        this.$set(this.editableSite, field, !!enabled);
      });
    },

    syncAllRolesFromRoleNotifications() {
      if (!this.editableSite) return;

      this.$set(
        this.editableSite,
        'allRoleNotificationsEnabled',
        ROLE_NOTIFICATION_FIELDS.every((field) => !!this.editableSite[field])
      );
    },

    async initializeEditableSite() {
      if (this.site && this.site._id) {
        this.editableSite = this.normalizeNotificationSettings({
          ...this.site,
        });
        return;
      }

      if (
        !Array.isArray(this.$store.state.sites) ||
        this.$store.state.sites.length === 0
      ) {
        await this.$store.dispatch('getSites');
      }

      const routeSiteId = String(this.$route.params?.id || '').trim();
      if (!routeSiteId) return;

      const foundSite = this.$store.state.sites.find(
        (currentSite) => String(currentSite?._id || '') === routeSiteId
      );

      if (foundSite) {
        this.editableSite = this.normalizeNotificationSettings({
          ...foundSite,
        });
      }
    },

    async updateSite() {
      if (!this.canEditSite) return;

      try {
        // eslint-disable-next-line no-unused-vars
        const { jobPhases, ...siteWithoutPhases } = this.editableSite;
        await this.$store.dispatch('updateSite', siteWithoutPhases);

        this.showSuccess = true;
        setTimeout(() => {
          this.$cookies.set(
            adminPageConstants.adminCookies.admin_selected_tab,
            adminPageConstants.adminPage.tabs.siteList
          );
          this.$router.push({
            name: 'admin-root',
          });
        }, 3000);
      } catch (err) {
        console.error(err);
        this.showFail = true;
      }
    },
    async deleteSite() {
      if (!this.canEditSite) return;

      this.$buefy.dialog.confirm({
        message: this.$t(`${TRANSLATION_ROOT}.delete_confirm`),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        confirmText: this.$t(`${TRANSLATION_ROOT}.delete`),
        onConfirm: async () => {
          try {
            await this.$store.dispatch('deleteSite', this.editableSite);

            this.successMessage = this.$t(`${TRANSLATION_ROOT}.deletesite`);
            this.showSuccess = true;
            setTimeout(() => {
              this.$cookies.set(
                adminPageConstants.adminCookies.admin_selected_tab,
                adminPageConstants.adminPage.tabs.siteList
              );
              this.$router.push({
                name: 'admin-root',
              });
            }, 3000);
          } catch (err) {
            console.error(err);
            this.failedMessage = this.$t(`${TRANSLATION_ROOT}.deletefail`);
            this.showFail = true;
          }
        },
      });
    },

    canDeletePhase(phase) {
      if (phase._id === 'inspection' || phase._id === 'repair') return false;
      return true;
    },

    handleClose() {
      this.$cookies.set(
        adminPageConstants.adminCookies.admin_selected_tab,
        adminPageConstants.adminPage.tabs.siteList
      );
      this.$router.push({
        name: 'admin-root',
      });
    },

    openAddPhaseModal() {
      this.isPhaseModalOpen = true;
    },

    async onAddPhase(phase) {
      try {
        await this.$store.dispatch(ADD_JOB_PHASE, {
          siteId: this.site._id,
          phase,
        });
        this.isPhaseModalOpen = false;
        this.$buefy.toast.open({
          message: 'Phase added successfully.',
          type: 'is-success',
          duration: 3000,
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Failed to add phase. Please try again.',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },

    confirmDeletePhase(phase) {
      this.$buefy.dialog.confirm({
        title: 'Delete Phase',
        message: `Are you sure you want to delete <b>${phase.name}</b>?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePhase(phase._id),
      });
    },

    async deletePhase(phaseId) {
      try {
        await this.$store.dispatch(DELETE_JOB_PHASE, {
          siteId: this.site._id,
          phaseId,
        });
        this.$buefy.toast.open({
          message: 'Phase deleted.',
          type: 'is-success',
          duration: 3000,
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Failed to delete phase.',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },

    openEditPhaseModal(phase) {
      this.editingPhase = phase;
    },

    async onRenamePhase({ name, reportSections }) {
      try {
        await this.$store.dispatch(UPDATE_JOB_PHASE, {
          siteId: this.site._id,
          phaseId: this.editingPhase._id,
          updates: { name, reportSections },
        });
        this.editingPhase = null;
        this.$buefy.toast.open({
          message: 'Phase renamed successfully.',
          type: 'is-success',
          duration: 3000,
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Failed to rename phase.',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },

    async toggleDefaultPhase(phaseId, isDefault) {
      try {
        await this.$store.dispatch(UPDATE_JOB_PHASE, {
          siteId: this.site._id,
          phaseId,
          updates: { isDefault },
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Failed to update phase.',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.close-form {
  position: fixed;
  width: 16px;
  height: 16px;
  right: 0;
  margin-right: 5%;
}

.close-form:hover {
  cursor: pointer;
}

.edit-site-container {
  margin-top: 2%;
  h4 {
    // margin-left: 5%;
    margin-top: 2%;
    margin-bottom: 4%;
    font-weight: bold;
    color: #333333;
    font-size: 20px;
  }
}

.edit-site-form-container {
  display: flex;
  flex-direction: row;
  width: 50%;
  margin-left: 10%;
}

.first-column {
  display: flex;
  width: 50%;
  flex-direction: column;
}

.column {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
}
select,
.select,
.is-empty {
  width: 100%;
}

.delete-update-buttons {
  right: 0;
  bottom: 0;
  position: absolute;
  margin-right: 10%;
  margin-bottom: 5%;
}

.dij-site-feature-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.job-phases-section {
  width: 60%;
  margin-left: 10%;
  margin-top: 24px;
  margin-bottom: 80px;

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    h4 {
      margin: 0;
    }
  }
}
.dij-site-notification-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.role-notification-sub-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 20px;
  padding-left: 12px;
  border-left: 2px solid #dbdbdb;
}
</style>
